import React, { useId } from 'react';
import { FaChevronRight, FaLock } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import '../../../styles/YourSongCard.css';

/** "#4" on the rail's numbering, or "Up next" for the locked song. */
const SpotLabel = ({ request }) => {
  const { t, format } = useLocale();
  return request.locked
    ? <span className="your-song-spot your-song-spot--next">{t('stream.yourSong.upNext')}</span>
    : <span className="your-song-spot">{t('stream.yourSong.spot', { spot: format.number(request.spot) })}</span>;
};

/** "$15 on it", "$20 on it · boosted +$5", or "Free request" (mockup 28, ENG-721). */
const moneyLine = (request, t, usd) => {
  if (!request.amountCents) return t('stream.yourSong.freeRequest');
  if (request.boostedCents > 0) {
    return t('stream.yourSong.onItBoosted', { amount: usd(request.amountCents), boost: usd(request.boostedCents) });
  }
  return t('stream.yourSong.onIt', { amount: usd(request.amountCents) });
};

/** Switch song ›, or "Switch locked" when the API says it can't switch. */
const SwitchControl = ({ request, onSwitch, compact = false }) => {
  const { t } = useLocale();
  if (!request.switchable) {
    if (!request.switchLockedReason) return null;
    return (
      <span className="your-song-switch your-song-switch--locked" data-testid="your-song-switch-locked">
        <FaLock aria-hidden="true" /> {t('stream.yourSong.switchLocked')}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="your-song-switch"
      onClick={() => onSwitch(request)}
      aria-label={t('stream.yourSong.switchLabel', { title: request.title })}
      data-testid="your-song-switch"
    >
      {compact ? t('stream.yourSong.switchShort') : t('stream.yourSong.switch')}
      <FaChevronRight className="rtl-mirror" aria-hidden="true" />
    </button>
  );
};

/**
 * The YOUR SONG card (mockups 09–11, ENG-683/685/687) in the performer card's
 * place while the listener has waiting requests. One request shows large:
 * cover, title, artist · year, the money on it (and any boost), its rail
 * number and how many songs play first. With several, the nearest leads and
 * the rest follow as rows, in play order. Each offers Switch song (into the
 * crate) unless the API has locked it (next up, or boosted).
 */
const YourSongCard = ({ requests = [], onSwitch }) => {
  const { t, format } = useLocale();
  const headingId = useId();
  if (!requests.length) return null;
  const usd = (cents) => format.usd(cents / 100, { cents: 'auto' });
  const [lead, ...rest] = requests;
  const artwork = resolveMediaUrl(lead.artworkUrl);
  const byline = [lead.artist, lead.releaseYear ? String(lead.releaseYear) : ''].filter(Boolean).join(' · ');
  const leadMoney = moneyLine(lead, t, usd);

  return (
    <section className="your-song" data-testid="your-song-card" aria-labelledby={headingId}>
      <h2 className="your-song-kicker" id={headingId}>
        {requests.length > 1
          ? t('stream.yourSong.titleMany', { count: requests.length, formattedCount: format.number(requests.length) })
          : t('stream.yourSong.title')}
      </h2>
      <div className="your-song-lead">
        {artwork
          ? <img className="your-song-cover" src={artwork} alt="" />
          : <span className="your-song-cover your-song-cover--blank" aria-hidden="true">♪</span>}
        <div className="your-song-body">
          <span className="your-song-name">{lead.title}</span>
          {byline && <span className="your-song-byline">{byline}</span>}
          {leadMoney && <span className="your-song-money" data-testid="your-song-money">{leadMoney}</span>}
        </div>
        <div className="your-song-place">
          <SpotLabel request={lead} />
          {!lead.locked && (
            <span className="your-song-away" data-testid="your-song-away">
              {t('stream.yourSong.away', { count: lead.songsAway, formattedCount: format.number(lead.songsAway) })}
            </span>
          )}
        </div>
      </div>
      <div className="your-song-actions">
        <SwitchControl request={lead} onSwitch={onSwitch} />
      </div>
      {rest.length > 0 && (
        <ol className="your-song-rows">
          {rest.map((request) => {
            const money = moneyLine(request, t, usd);
            const away = request.locked
              ? ''
              : t('stream.yourSong.awayShort', { count: request.songsAway, formattedCount: format.number(request.songsAway) });
            const meta = away && money ? t('stream.yourSong.rowMeta', { away, money }) : away || money;
            return (
              <li key={request.entryId} className="your-song-row" data-testid="your-song-row">
                <SpotLabel request={request} />
                <span className="your-song-row-title">{request.title}</span>
                <span className="your-song-row-meta">{meta}</span>
                <SwitchControl request={request} onSwitch={onSwitch} compact />
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

export default YourSongCard;
